import { EMPTY_RANGE, Range } from "../../widgets/RangeFilter";

// The filters both stone searches offer, beyond the color itself.
export type StoneFilters = {
  availableOnly: boolean;
  giaOnly: boolean;
  patternOnly: boolean;
  shape: string | null;
  price: Range;
  carat: Range;
  clarity: Range;
};

// A search starts on the stones someone could actually sell: the stock is full of stones that have
// gone, and they are rarely what a search is for. Every other filter starts open.
export const DEFAULT_FILTERS: StoneFilters = {
  availableOnly: true,
  giaOnly: false,
  patternOnly: false,
  shape: null,
  price: EMPTY_RANGE,
  carat: EMPTY_RANGE,
  clarity: EMPTY_RANGE,
};

// The stones a search ranks at all: Langerman's, and whatever the filters narrow that to. The
// color systems are not here — they are arguments of the query, which applies them before the rows
// are read rather than to the ranked projection.
//
// Clauses are joined with the symbol operators, as the saved queries are: the backend's filter
// parser takes "and"/"or" too, but not straight after a true, false or null.
export function filterExpression(
  filters: StoneFilters,
  clarities: string[],
): string {
  const clauses = [
    'diamondAnalysis.diamond != null && diamondAnalysis.diamond.source == "Langerman"',
  ];

  if (filters.availableOnly) {
    clauses.push("diamondAnalysis.diamond.noLongerAvailable == false");
  }
  if (filters.giaOnly) {
    clauses.push("diamondAnalysis.diamond.isGIACertified == true");
  }
  if (filters.shape) {
    clauses.push(`diamondAnalysis.diamond.shape == "${filters.shape}"`);
  }
  for (const [field, range] of [
    ["price", filters.price],
    ["carat", filters.carat],
  ] as const) {
    if (range.min !== null) {
      clauses.push(`diamondAnalysis.diamond.${field} >= ${Number(range.min)}`);
    }
    if (range.max !== null) {
      clauses.push(`diamondAnalysis.diamond.${field} <= ${Number(range.max)}`);
    }
  }

  // Clarity grades are names, so a range is the span of the ordered list between the two bounds,
  // written out as the grades it contains.
  const from = filters.clarity.min ? clarities.indexOf(filters.clarity.min) : 0;
  const to = filters.clarity.max
    ? clarities.indexOf(filters.clarity.max)
    : clarities.length - 1;
  const span = clarities.slice(Math.max(from, 0), to + 1);
  if (span.length > 0 && span.length < clarities.length) {
    clauses.push(
      `(${span
        .map((grade) => `diamondAnalysis.diamond.clarity == "${grade}"`)
        .join(" || ")})`,
    );
  }

  return clauses.join(" && ");
}
